import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { CareDeskState } from './care-desk-model';

// The care desk's one-line goal (feat/care-desk K5; the hotel goal-bar grammar):
// a segmented bar (card live → blocked on KYC → handled outside → to issue) +
// the amber number the operator works to zero. All-green when everyone is cared
// for. Token-native; same semantic hues as the rest of the desk.

const SEGMENTS: {
  key: 'LIVE' | 'BLOCKED_KYC' | 'HANDLED_OUTSIDE' | 'REMAINDER';
  labelKey: string;
  barClass: string;
  dotClass: string;
}[] = [
  {
    key: 'LIVE',
    labelKey: 'goal.segment.live',
    barClass: 'bg-emerald-500',
    dotClass: 'bg-emerald-500',
  },
  {
    key: 'BLOCKED_KYC',
    labelKey: 'goal.segment.blockedKyc',
    barClass: 'bg-sky-500',
    dotClass: 'bg-sky-500',
  },
  {
    key: 'HANDLED_OUTSIDE',
    labelKey: 'goal.segment.handledOutside',
    barClass: 'bg-slate-400 dark:bg-slate-500',
    dotClass: 'bg-slate-400 dark:bg-slate-500',
  },
  {
    key: 'REMAINDER',
    labelKey: 'goal.segment.remainder',
    barClass: 'bg-amber-500',
    dotClass: 'bg-amber-500',
  },
];

export function CareGoalBar({ desk, truncated }: { desk: CareDeskState; truncated: boolean }) {
  const { t } = useTranslation('care');

  // An EMPTY walk is not a victory — a cold roster / upstream blip must read as
  // "nothing loaded", never as "everyone cared for".
  if (desk.rows.length === 0) {
    return (
      <section
        className="rounded-lg border border-border bg-card px-4 py-3"
        data-testid="care-goal-bar"
      >
        <p className="text-sm text-muted-foreground">{t('goal.coldRoster')}</p>
      </section>
    );
  }

  const values: Record<(typeof SEGMENTS)[number]['key'], number> = {
    // A live card is a live card — the discretionary ATTENTION overlay stays green here.
    LIVE: desk.counts.ISSUED.pnrs + desk.counts.ATTENTION.pnrs,
    BLOCKED_KYC: desk.counts.BLOCKED_KYC.pnrs,
    HANDLED_OUTSIDE: desk.counts.HANDLED_OUTSIDE.pnrs,
    REMAINDER: desk.remainderPnrs,
  };
  const total = Math.max(1, desk.goalPnrs);

  return (
    <section
      className={cn(
        'rounded-lg border px-4 py-3',
        desk.allSettled
          ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
          : 'border-border bg-card',
      )}
      data-testid="care-goal-bar"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {desk.allSettled ? (
          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
            {t('goal.settled')}
          </p>
        ) : (
          <p className="text-sm">
            <span
              className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400"
              data-testid="care-goal-remainder"
            >
              {desk.remainderPnrs}
            </span>{' '}
            <span className="font-semibold">
              {t('goal.remainder', { count: desk.remainderPnrs })}
            </span>{' '}
            <span className="text-muted-foreground">
              {t('goal.remainderFacts', {
                pax: desk.remainderPax,
                blocked: desk.counts.BLOCKED_KYC.pnrs,
              })}
            </span>
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {t('goal.entitled', { pnrs: desk.goalPnrs, pax: desk.goalPax })}
          {truncated ? t('goal.partialRoster') : ''}
        </p>
      </div>
      <div className="mt-3 flex h-2 w-full gap-px overflow-hidden rounded-full bg-muted">
        {SEGMENTS.map((seg) =>
          values[seg.key] > 0 ? (
            <div
              key={seg.key}
              className={cn('h-full', seg.barClass)}
              style={{ width: `${(values[seg.key] / total) * 100}%` }}
              title={`${t(seg.labelKey)}: ${values[seg.key]}`}
            />
          ) : null,
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
        {SEGMENTS.map((seg) => (
          <span
            key={seg.key}
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
          >
            <span aria-hidden className={cn('h-2 w-2 rounded-full', seg.dotClass)} />
            {values[seg.key]} {t(seg.labelKey)}
          </span>
        ))}
      </div>
    </section>
  );
}
